import {
  a11y,
  authGeometry,
  authPanel,
  breakpoints,
  createTheme,
  grid,
  gridExceptions,
  gridExceptionThresholdFor,
  layer,
  layout,
  motion,
  sectionHeading,
  tooltip,
  typography,
  type BrandTokens,
  type ThemeMode,
} from '@icebreaker/shared/design';
import {
  createWebColors,
  createWebShadows,
  legacyRadiusPx,
  railCssDeclarations,
  siteGutterRules,
  wideRailRules,
  webFontFamilies,
  webFontStack,
  webRadius,
  webSize,
  webSpace,
  webAvatarSize,
  webBrandMarkColors,
  webTypeStyles,
  type RailProfile,
} from '@icebreaker/shared/design/web-tema';

/**
 * TOKEN BORU HATTI — `--ib-*` CSS değişkenlerini üreten TEK yer.
 *
 * Kitin CSS modülleri yalnız `var(--ib-*)` okur; değişkenleri BASAN kod budur.
 * Eskiden web'deydi; yönetim paneli (`apps/admin`) aynı kiti kullandığı için
 * buraya çıktı (kopya olsaydı token eklendiğinde biri sessizce eskirdi).
 *
 * MARKA ÇAĞIRANDAN GELİR (`BrandTokens`): kit hiçbir uygulamanın env katmanını
 * bilmez — tıpkı yönlendiriciyi (`link-slot.ts`) ve çevirmeni
 * (`i18n-context.tsx`) bilmediği gibi.
 *
 * Kalıcı kontrol: `apps/web/src/shared/theme/token-usage.guard.spec.ts` bu
 * fonksiyonların ÜRETTİĞİ kümeyi CSS modüllerinin OKUDUĞU kümeyle karşılaştırır.
 *
 * WEB TEMASI (yeni görünüm, Aşama 0): renk, gölge, boşluk, köşe, boy ve yazı
 * değerleri `@icebreaker/shared/design/web-tema`dan gelir (WarmBreak tasarım
 * sistemi). Mobilin `createTheme`i donmuştur ve buradan okunmaz. Eski adlardan
 * kalanlar (QR renkleri, `--ib-radius-md`, `--ib-radius-lg`) yeni değerlerden
 * basılır, kaynağı ve gerekçesi `web-tema/eski-anahtarlar.ts`te (6a temizliği).
 *
 * `createThemes` mobil tema nesnesini döndürmeye devam eder: tek okuyucusu
 * katılım belgesi önizlemesidir ve belge PDF'i eski renkte kalır (plan K4).
 */
export function createThemes(brand: BrandTokens) {
  return {
    light: createTheme('light', brand),
    dark: createTheme('dark', brand),
  } as const;
}

export type ThemeSet = ReturnType<typeof createThemes>;

/**
 * Tokenları CSS değişkenine çevirir; bileşenler yalnızca `var(--ib-*)` kullanır.
 * Ölçek tokenları (boşluk, köşe, tipografi) moddan bağımsızdır ve `:root`'ta bir
 * kez yazılır; renk ve derinlik moda göre değişir. `rails`: `web` ya da `admin` (yerlesim.ts).
 */
export function themeCssVariables(
  mode: ThemeMode,
  brand: BrandTokens,
  rails: RailProfile = 'web',
): string {
  return [...themeColorVariables(mode, brand), ...scaleCssVariables(rails)].join('\n');
}

/** Yalnızca moda bağlı değişkenler — `[data-theme='dark']` bloğu bunu kullanır. */
export function themeColorCssVariables(mode: ThemeMode, brand: BrandTokens): string {
  return themeColorVariables(mode, brand).join('\n');
}

/**
 * KIRILIM KATMANI — Revizyon 2 (`docs/tasarim-kirilim-sozlesmesi.md`).
 *
 * `:root` bloğunun İÇİNE yazılamayan her kural burada üretilir (medya sorgusu
 * ve konteyner sorgusu bildirim listesi değildir); çağıran bunu `:root{…}`
 * bloğunun ARDINA ekler. Taban basamaklar `:root`'ta durur, buradaki her
 * basamak onları ezer — sıra artan `minWidth`tir, son eşleşen kazanır.
 *
 * BEŞ ŞEY ÜRETİLİR:
 *   1. yan dolgu merdiveni (`--ib-gutter`)
 *   2. kolon arası merdiveni (`--ib-gap`)
 *   3. ray basamakları: admin xxl (`min(78vw,1760px)`), web site kenarı xl'de 40
 *   4. IZGARA — `@container (min-width: 760px)`. Konteyner sorgusunun eşiği
 *      `var()` kabul ETMEZ (spesifikasyon gereği), bu yüzden literal token
 *      DEĞERİNDEN burada basılır; modülde elle yazılmış sayı yoktur.
 *   5. IZGARA İSTİSNALARI — `gridExceptions` kaydındaki her yüzey için
 *      `[data-grid-exception="…"]` kuralı (kolon tavanı · öksüz satır kuralının
 *      geri alınması · sabit kapak yüksekliği). Kayıtta olmayan istisna
 *      üretilmez; kayıt boşsa hiçbir kural basılmaz.
 *   6. YIĞIN KARTININ EYLEMİ — `@container (min-width: 340px)`. Eşik yine
 *      token'dan (`grid.cardMin`) basılır; kart modülü sayı yazmaz.
 */
export function layoutLadderCssVariables(rails: RailProfile = 'web'): string {
  const rules: string[] = [];

  for (const step of layout.gutterLadder.filter((entry) => entry.minWidth > 0)) {
    rules.push(`@media (min-width:${step.minWidth}px){:root{--ib-gutter:${step.padding}px;}}`);
  }
  for (const step of layout.gapLadder.filter((entry) => entry.minWidth > 0)) {
    rules.push(`@media (min-width:${step.minWidth}px){:root{--ib-gap:${step.gap}px;}}`);
  }
  rules.push(...wideRailRules(rails), ...siteGutterRules(rails));
  /*
   * BÖLÜM BAŞLIĞI — `l` (1024+) basamağında ÜST BOŞLUK 32 → 48 (SPEC "Kırılım").
   * Merdivenin tek basamağı budur: punto, ağırlık, renk ve ayırıcı kalınlığı
   * kırılımla DEĞİŞMEZ, o yüzden burada yalnız boşluk döner.
   */
  rules.push(
    `@media (min-width:${breakpoints.l}px){:root{--ib-section-space-before:${sectionHeading.spaceBefore.l}px;}}`,
  );
  // Bölüm başlığının telefon basamağı (`h3-m` 20/26, `h2-m` 26/32).
  rules.push(
    `@media (max-width:${breakpoints.md - 1}px){:root{--ib-section-heading-size:var(--ib-type-h3-m-size);--ib-section-heading-leading:var(--ib-type-h3-m-line);--ib-section-heading-h2-size:var(--ib-type-h2-m-size);--ib-section-heading-h2-leading:var(--ib-type-h2-m-line);}}`,
  );
  /*
   * IZGARA TAVANI: `GRID_MAX` bir tavan KURALIDIR, `auto-fill`den türetilmez.
   * `repeat(auto-fill, minmax(340px,1fr))` 1108px'lik orta kolonda ÜÇ kolon
   * üretirdi; tasarımcı iki kolonda durmayı açıkça kural yaptı.
   *
   * İKİNCİ KURAL — tarih bloğu: "İki kolonda tarih bloğu 52 px kareden SATIR
   * İÇİ ÇİPE döner" (Revizyon 2 §4). Aynı konteyner sorgusundan gelir, ayrı
   * bir eşik İCAT EDİLMEZ. Genel kanca sınıfları YALNIZ bu üçtür ve burada
   * tanımlıdır; CSS modülleri konteyner sorgusu YAZMAZ (eşik `var()` kabul
   * etmediği için modülde literal doğardı).
   */
  rules.push(
    [
      `@container (min-width:${grid.min}px){`,
      /*
       * Seçici BİLİNÇLİ olarak iki kez yazılır (`.a.a` → özgüllük 0,2,0):
       * kural `<head>`e enjekte edilir ve CSS modülünün kendi sınıfıyla AYNI
       * özgüllükte olsaydı kaynak sırası kazanırdı. Ölçüldü: 1920'de orta
       * kolon 846 px'e çıkmış olmasına rağmen ızgara tek kolonda kalıyordu.
       */
      `.ib-stack-grid.ib-stack-grid{grid-template-columns:repeat(${grid.max},minmax(0,1fr));}`,
      /*
       * TEK KALAN KART SATIRI DOLDURUR (elle test, 31.08 — kullanıcı buldu).
       *
       * İki kolonlu ızgarada TEK SAYIDA kart olduğunda sonuncusunun yanında
       * bir kart genişliğinde delik kalıyordu. Ölçüldü: 1512'de üç öneri
       * kartında ~380 px'lik boşluk, üstelik sağ ray daha yukarıda bittiği
       * için sayfanın sağ alt köşesi tamamen boşalıyordu.
       *
       * Kural SINIF düzeyinde: bu ızgarayı kullanan her yüzeyde geçerli,
       * yalnız ana sayfada değil. Kart GİZLEMEK yerine yaymayı seçtik —
       * çift sayıya zorlamak gerçek bir öneriyi kullanıcıdan saklardı ve
       * tek öneri olan durumda bölümü tamamen boşaltırdı (md. 9).
       *
       * `:last-child:nth-child(odd)` yalnız TEK BAŞINA kalan sonuncuyu
       * yakalar: çift sayıda kartta sonuncu `nth-child(even)`dır ve kural
       * hiç uygulanmaz.
       */
      `.ib-stack-grid.ib-stack-grid > :last-child:nth-child(odd){grid-column:1/-1;}`,
      /*
       * Yayılan kartın KAPAĞINA TAVAN (elle test, 31.08 — kullanıcı buldu).
       *
       * Deliği kapatmak için tek kalan kartı satıra yaymak yetmedi: kart iki
       * kat genişleyince 16/9 kapak da iki kat uzuyor ve diğer kartların
       * yanında orantısız duruyor (ölçüldü: 1512'de ~380px). Yayılan kartta
       * kapak oranı 32/9'a çıkar — genişlik iki katına çıktığı için yükseklik
       * aynı kalır, kart komşularıyla aynı ritimde okunur.
       */
      `.ib-stack-grid.ib-stack-grid > :last-child:nth-child(odd) [class*="cover"]{aspect-ratio:32/9;}`,
      `.ib-stack-grid .ib-date-square{display:none;}`,
      `.ib-stack-grid .ib-date-chip{display:inline-flex;}`,
      `}`,
    ].join(''),
  );
  rules.push(cardFootRule());
  rules.push(...gridExceptionRules());
  rules.push(priorityTableRule());
  return rules.join('\n');
}

/**
 * KOLON ÖNCELİKLİ TABLO — `@container (min-width: 1100px)` (`layout.priorityTable`).
 *
 * Kanca üç sınıftır: kap `ib-priority-table` (konteyner budur), düşük öncelikli
 * kolon hücresi `ib-col-optional` ve dar hâlde gizlenen kolon sayısını söyleyen
 * not `ib-col-note`. Taban hâl (kolon gizli, not görünür) MODÜLDE yazılır;
 * burada yalnız geniş hâl döner. Seçici özgüllüğü (0,3,0) modülün tek
 * sınıflık tabanını (0,1,0) kaynak sırasından bağımsız ezer. Eşik literal
 * token değerinden basılır, modülde `@container` yazılmaz.
 */
function priorityTableRule(): string {
  return [
    `.ib-priority-table{container-type:inline-size;}`,
    `@container (min-width:${layout.priorityTable.min}px){`,
    `.ib-priority-table .ib-col-optional.ib-col-optional{display:table-cell;}`,
    `.ib-priority-table .ib-col-note.ib-col-note{display:none;}`,
    `}`,
  ].join('');
}

/**
 * YIĞIN KARTININ EYLEMİ — `md` ve üstünde satır içi, sağa yaslı.
 *
 * GÜNCEL DURUM (24.09): eylem yeniden ÜST KATTA, başlık + meta bloğunun
 * sağındadır (kullanıcı kararı 17.09; 21.09 turu onu alt kata taşımıştı,
 * 24.09'da geri alındı). Kart modülünde artık konteyner YOKTUR (`.foot`un
 * `container-type`ı kalktı), kural en yakın ata konteyneri sorgular. Modül
 * tabanı `md` ve üstünde zaten `flex: none` + içerik enindedir, bu yüzden
 * bu kural bugün tabanla AYNI sonucu verir (`event-stack-card.module.css`,
 * `.lede` yorumu). Kaldırılması ayrı bir iştir: kapı
 * (`list-anatomy.guard.spec.ts`) kuralın varlığını asertler.
 *
 * `md` ALTINDA HİÇ ÇALIŞMAZ: `sm` kırılımında eylem TAM GENİŞLİKTİR (Revizyon
 * 2 §4) ve bu bir KIRILIM kararıdır; modül onu `@media (max-width: 767px)`
 * içinde kurar.
 *
 * Seçici İKİ KEZ yazılır (0,2,0): kural `<head>`e enjekte edilir, CSS
 * modülünün kendi sınıfıyla aynı özgüllükte olsaydı kaynak sırası kazanırdı
 * (aynı gerekçe `.ib-stack-grid.ib-stack-grid` için de geçerli).
 */
function cardFootRule(): string {
  return [
    `@media (min-width:${breakpoints.md}px){`,
    `@container (min-width:${grid.cardMin}px){`,
    `.ib-card-action.ib-card-action{width:auto;margin-left:auto;}`,
    `.ib-card-action.ib-card-action>*{flex:none;}`,
    `}`,
    `}`,
  ].join('');
}

/**
 * IZGARA İSTİSNALARI — `gridExceptions` (Revizyon 2 sözleşmesine BEYANLI
 * istisna; `docs/anasayfa-izgara-karari-2026-09-06.md`).
 *
 * Seçici `.ib-stack-grid.ib-stack-grid[data-grid-exception="…"]` (0,3,0):
 * taban kuralı (0,2,0) ve öksüz satır kuralını (0,4,0 → burada 0,5,0 ile
 * ezilir) yener; CSS modülleri konteyner sorgusu yazmadığı için başka bir
 * kaynakla yarışmaz.
 *
 *  · `fit` yalnız `auto`: referansın `auto-fit`i, ama TAVANLI — asgari ray
 *    genişliği `max(cardMin, (100% − (max−1)·gap) / max)`; tarayıcı `max`tan
 *    çok ray açamaz. Boş ray kapanır, kartlar yayılır. (`fixed` dalı — kolon
 *    sayısı başına ayrı konteyner eşiği — 27. kaçamak turunda SİLİNDİ: kayıt
 *    yoktu, hiçbir test dalı çalıştırmıyordu; gerekirse tiple birlikte geri
 *    gelir.)
 *  · ÖKSÜZ SATIR KURALI YALNIZ TAVAN AŞILINCA GERİ ALINIR (eşik
 *    `gridExceptionThresholdFor(key, grid.max + 1)`): üçüncü rayda tek
 *    kalan kart kendi rayında durur, yayılmaz (yayılsaydı 1116 px'lik kart ve
 *    şişen kapak geri gelirdi — O7). Konteyner sözleşme kolonunda kaldığında
 *    (ray = grid.max) yüzey SÖZLEŞMENİN KENDİSİDİR ve sözleşmenin öksüz
 *    kuralı (31.08 kullanıcı bulgusu: delik yerine yayılma) olduğu gibi kalır
 *    — istisna sözleşmeyi dar ekranda da ezmez. Eşiğin YERİ `cardMin`den
 *    türer: öneri ızgarasında 300 → 932 px; 1024 px ekranın 949 px akışı
 *    eşiğin ÜSTÜNDEDİR (QA P2-2: 320 ile eşik 992'ydi ve 949'da üçüncü kart
 *    6,36:1 kapakla yayılıyordu). Kalan bant 760–931 px konteyner (bkz.
 *    `ana-sayfa-kolon-bosluk` "ÖKSÜZ BANDI" bilinen sınırı, iki yönlü).
 *  · `fit: 'fill'` referansın SABİT raylarıdır: `auto-fill` (boş ray kapanmaz)
 *    ve öksüz sıfırlaması `grid.min`den itibaren — ızgara açık olduğu her
 *    genişlikte kart bir kolon genişliğinde kalır (organizatör sayfası, QA
 *    P2-2: `auto`da 1440'ta tek kart 1116 px banda, 1024'te iki kolona yayıldı).
 *  · `coverHeight`: kart modülü `--ib-stack-cover-height` okur
 *    (`event-stack-card.module.css`); değişken yalnız istisnalı ızgarada
 *    tanımlıdır, başka yerde kapak 16:9 kalır.
 */
function gridExceptionRules(): string[] {
  const rules: string[] = [];
  for (const [key, rule] of Object.entries(gridExceptions)) {
    const sel = `.ib-stack-grid.ib-stack-grid[data-grid-exception="${key}"]`;
    const orphanReset = `${sel} > :last-child:nth-child(odd){grid-column:auto;}`;
    const typedKey = key as keyof typeof gridExceptions;
    const minTrack = `max(${rule.cardMin}px,calc((100% - ${(rule.max - 1) * grid.cardGap}px) / ${rule.max}))`;
    const fill = rule.fit === 'fill';
    rules.push(
      `@container (min-width:${grid.min}px){${sel}{grid-template-columns:repeat(${fill ? 'auto-fill' : 'auto-fit'},minmax(${minTrack},1fr));}}`,
    );
    const resetFrom = fill ? grid.min : gridExceptionThresholdFor(typedKey, grid.max + 1);
    rules.push(`@container (min-width:${resetFrom}px){${orphanReset}}`);
    if (rule.coverHeight !== null) {
      rules.push(`${sel}{--ib-stack-cover-height:${rule.coverHeight}px;}`);
    }
  }
  return rules;
}

function themeColorVariables(mode: ThemeMode, brand: BrandTokens): string[] {
  const lines: string[] = [];
  const shadows = createWebShadows(mode);

  for (const [key, value] of Object.entries(createWebColors(mode, brand))) {
    lines.push(`--ib-color-${kebab(key)}: ${value};`);
  }
  // Logo işareti (K3): iki temada ve bantta AYNI üç renk, kaynak `webBrandMarkColors`.
  const mark = webBrandMarkColors(brand);
  for (const prefix of ['mark', 'markBand']) {
    lines.push(`--ib-${kebab(prefix)}-square: ${mark.square};`);
    lines.push(`--ib-${kebab(prefix)}-ink: ${mark.mark};`);
    lines.push(`--ib-${kebab(prefix)}-accent: ${mark.accent};`);
  }
  lines.push(`--ib-device-bezel: ${createWebColors('light', brand).textPrimary};`); // DeviceFrames kasası
  for (const [key, value] of Object.entries(shadows)) {
    lines.push(`--ib-${kebab(key)}: ${value};`);
  }
  /*
   * İPUCU RENKLERİ — kartı yok, yapı korunur (plan Aşama 1a, Tooltip satırı).
   * Eski ipucu tasarımının iki hâli (`design/tooltip.ts` `tooltipColors`)
   * web temasının adlarıyla kurulur: açık temada koyu mürekkep kutu, koyu
   * temada yükseltilmiş yüzey ve güçlü kenarlık. `tooltipColors` eski
   * palete bağlıdır, web teması onu okumaz.
   */
  for (const [key, value] of Object.entries(webTooltipColors(mode, brand))) {
    lines.push(`--ib-color-tooltip-${kebab(key)}: ${value};`);
  }
  return lines;
}

/** İpucu kutusunun üç rengi, web temasından (`--ib-color-tooltip-*`). */
export function webTooltipColors(
  mode: ThemeMode,
  brand: BrandTokens,
): { readonly surface: string; readonly text: string; readonly border: string } {
  const colors = createWebColors(mode, brand);
  return mode === 'dark'
    ? { surface: colors.surfaceRaised, text: colors.textPrimary, border: colors.borderStrong }
    : { surface: colors.textPrimary, text: colors.background, border: colors.textPrimary };
}

/** Moddan bağımsız ölçekler. */
function scaleCssVariables(rails: RailProfile): string[] {
  const lines: string[] = [];
  // Tasarım ölçekleri: anahtar tasarım adıdır (`space-0_5`, `radius-card`, `control-height`).
  for (const [key, value] of Object.entries({ ...webSpace, ...webRadius, ...webSize })) {
    lines.push(`--ib-${kebab(key)}: ${value}px;`);
  }
  // Eski köşe adlarından kalanlar (`web-tema/eski-anahtarlar.ts`).
  for (const [key, value] of Object.entries(legacyRadiusPx())) {
    lines.push(`--ib-radius-${kebab(key)}: ${value}px;`);
  }
  /*
   * YAZI STİLLERİ (23): `--ib-type-<stil>-size|line|weight|tracking|family`.
   * Aile değişkeni rol değişkenini okur; yazı tipi dosyası değişirse stil değişmez.
   */
  for (const [key, style] of Object.entries(webTypeStyles)) {
    lines.push(`--ib-type-${kebab(key)}-size: ${style.fontSize}px;`);
    lines.push(`--ib-type-${kebab(key)}-line: ${style.lineHeight}px;`);
    lines.push(`--ib-type-${kebab(key)}-weight: ${style.fontWeight};`);
    lines.push(`--ib-type-${kebab(key)}-tracking: ${style.letterSpacing};`);
    lines.push(`--ib-type-${kebab(key)}-family: var(--ib-font-${kebab(style.family)});`);
  }
  for (const [key, value] of Object.entries(webAvatarSize)) {
    lines.push(`--ib-avatar-${kebab(key)}: ${value}px;`);
  }
  for (const [key, value] of Object.entries(typography.fontSize)) {
    lines.push(`--ib-font-size-${kebab(key)}: ${value}px;`);
  }
  for (const [key, value] of Object.entries(typography.fontWeight)) {
    lines.push(`--ib-font-weight-${kebab(key)}: ${value};`);
  }
  for (const [key, value] of Object.entries(typography.lineHeight)) {
    lines.push(`--ib-line-height-${kebab(key)}: ${value};`);
  }
  /*
   * Eski harf aralığı adları (`tokens.ts`, mobil donmuş) webde 0 basılır:
   * kullanıcı kararı 30.10.2026, başlık, metin ve etikette harf aralığı yok.
   */
  for (const key of Object.keys(typography.letterSpacing)) {
    lines.push(`--ib-letter-spacing-${kebab(key)}: 0;`);
  }
  for (const [key, value] of Object.entries(motion.duration)) {
    lines.push(`--ib-motion-${kebab(key)}: ${value}ms;`);
  }
  for (const [key, value] of Object.entries(motion.easing)) {
    lines.push(`--ib-easing-${kebab(key)}: ${value};`);
  }
  // Raylar (profile göre, `web-tema/yerlesim.ts`); üst basamak `layoutLadderCssVariables()`.
  lines.push(...railCssDeclarations(rails));
  // Beş kırılım — kabuk medya sorgularının TEK kaynağı (Revizyon 2 §2).
  for (const [key, value] of Object.entries(breakpoints)) {
    lines.push(`--ib-bp-${kebab(key)}: ${value}px;`);
  }
  // Izgara sözleşmesi (§3): eşik konteyner sorgusunda, değişkenler kart ölçüsü ve boşluk.
  lines.push(`--ib-grid-min: ${grid.min}px;`);
  lines.push(`--ib-grid-card-min: ${grid.cardMin}px;`);
  lines.push(`--ib-grid-card-gap: ${grid.cardGap}px;`);
  lines.push(`--ib-grid-max: ${grid.max};`);
  lines.push(`--ib-mid-min: ${grid.midMin}px;`);
  // İçerik sütunları — ray DEĞİL, rayın içindeki kolon (§8.4 #29/#30).
  for (const [key, value] of Object.entries(layout.columns)) {
    lines.push(`--ib-column-${kebab(key)}: ${value}px;`);
  }
  lines.push(`--ib-gutter: ${baseGutter()}px;`);
  lines.push(`--ib-gap: ${baseGap()}px;`);
  for (const [key, value] of Object.entries(layout.rhythm)) {
    lines.push(`--ib-rhythm-${kebab(key)}: ${value}px;`);
  }
  lines.push(`--ib-settings-nav: ${layout.settingsNavWidth}px;`);
  // Dokunma alanı tabanı (README 4.9, `a11y.minTouchTargetPt`): küçük ikon düğmelerinin isabet alanı.
  lines.push(`--ib-touch-target: ${a11y.minTouchTargetPt}px;`);
  /*
   * BÖLÜM BAŞLIĞI RİTMİ (`design/section-heading.ts`, karar 14.09.2026).
   * Üst boşluğun `l` basamağı `layoutLadderCssVariables()` içinde medya
   * sorgusundan gelir — burada TABAN basamak basılır, tıpkı dolgu/ray
   * merdiveninde olduğu gibi. Punto ve ağırlık kırılımla DEĞİŞMEZ, o yüzden
   * merdivende değil burada durur.
   */
  // Başlık ölçeği web yazı stilinden (1b): varsayılan `h3`, `scale="h2"`; mobilin 24/600'ü donmuş.
  for (const [name, style] of [
    ['heading', 'h3'],
    ['headingH2', 'h2'],
  ] as const) {
    for (const [prop, from] of [
      ['size', 'size'],
      ['weight', 'weight'],
      ['leading', 'line'],
      ['tracking', 'tracking'],
    ] as const) {
      lines.push(`--ib-section-${kebab(name)}-${kebab(prop)}: var(--ib-type-${style}-${from});`);
    }
  }
  lines.push(`--ib-section-divider-width: ${sectionHeading.dividerWidth}px;`);
  lines.push(`--ib-section-divider-gap: ${sectionHeading.dividerGap}px;`);
  lines.push(`--ib-section-content-gap: ${sectionHeading.contentGap}px;`);
  lines.push(`--ib-section-space-before: ${sectionHeading.spaceBefore.base}px;`);
  /*
   * KİMLİK EKRANLARI — VAAT YÖNÜ (`design/auth-panel.ts`). Geometri (400 form,
   * 72/40 kolon dolgusu, 320 bant) ve panel renkleri TEK BLOKTA, moddan bağımsız
   * basılır: koyu panel açık temada da koyudur (bilinçli karar 07.09.2026).
   */
  for (const [key, value] of Object.entries(authGeometry)) {
    lines.push(`--ib-auth-${kebab(key)}: ${value}px;`);
  }
  for (const [key, value] of Object.entries(authPanel)) {
    lines.push(`--ib-auth-panel-${kebab(key)}: ${value};`);
  }
  /*
   * YIĞIN SIRASI + İPUCU (`docs/design/ipucu/…Tooltip Bileşeni.dc.html`).
   * Adlar referansın `--ib-tip-*` / `--ib-z-*` listesidir (`design/tooltip.ts`).
   * Renkler burada DEĞİL: moda bağlı oldukları için `--ib-color-tooltip-*`
   * olarak renk bloğundan gelir; gölge `--ib-shadow-2`, süre
   * `--ib-motion-fast`tır.
   */
  for (const [key, value] of Object.entries(layer)) {
    lines.push(`--ib-z-${kebab(key)}: ${value};`);
  }
  lines.push(`--ib-tip-max-w: ${tooltip.maxWidth}px;`);
  lines.push(`--ib-tip-pad-y: ${tooltip.paddingY}px;`);
  lines.push(`--ib-tip-pad-x: ${tooltip.paddingX}px;`);
  lines.push(`--ib-tip-radius: ${tooltip.radius}px;`);
  lines.push(`--ib-tip-font-size: ${tooltip.fontSize}px;`);
  lines.push(`--ib-tip-line: ${tooltip.lineHeight};`);
  lines.push(`--ib-tip-weight: ${tooltip.fontWeight};`);
  lines.push(`--ib-tip-arrow: ${tooltip.arrow}px;`);
  lines.push(`--ib-tip-offset: ${tooltip.offset}px;`);
  lines.push(`--ib-tip-edge: ${tooltip.edge}px;`);
  lines.push(`--ib-tip-shift: ${tooltip.shift}px;`);
  lines.push(`--ib-text-min: ${layout.textBlockMinWidth}px;`);
  lines.push(`--ib-text-max: ${layout.textBlockMaxWidth}px;`);
  /*
   * Sistem fontu (kullanıcı kararı 30.10.2026): yükleyici yok. Tek yığın
   * tasarımın `--font-system` değişkenidir (boruda `--ib-font-system`), üç rol
   * ona bağlanır. Yükleyicinin doldurduğu ara değişkenler yazı tipi
   * dosyalarıyla birlikte kalktı (kapı: `local-fonts.guard.spec.ts`).
   */
  lines.push(`--ib-font-system: ${webFontStack};`);
  for (const role of Object.keys(webFontFamilies)) {
    lines.push(`--ib-font-${kebab(role)}: var(--ib-font-system);`);
  }
  return lines;
}

/** Merdivenin ilk (medya sorgusuz) basamağı. */
function baseGutter(): number {
  const first = layout.gutterLadder.find((step) => step.minWidth === 0);
  if (!first) throw new Error('gutterLadder must define a minWidth:0 step');
  return first.padding;
}

/** Kolon arası merdiveninin ilk (medya sorgusuz) basamağı. */
function baseGap(): number {
  const first = layout.gapLadder.find((step) => step.minWidth === 0);
  if (!first) throw new Error('gapLadder must define a minWidth:0 step');
  return first.gap;
}

/** camelCase ve tasarım adındaki `_` (`space-0_5`) kebab'a döner. */
function kebab(value: string): string {
  return value.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`).replace(/_/g, '-');
}
